import professionalsContract from "../../contracts/professionals.json";

export const EVENT_BTN_TYPES = [
  {
    id: 0,
    label: "Round Robin",
    description: "Each user will match with every other user in the group",
  },
  {
    id: 1,
    label: "Match",
    description: "Users will  match based on match type",
  },
];

export const SESSION_TIME_BTNS = [
  { id: 240, label: "4", description: "min" },
  { id: 360, label: "6", description: "min" },
  { id: 480, label: "8", description: "min" },
  { id: 600, label: "10", description: "min" },
  { id: 900, label: "15", description: "min" },
];

export const MATCH_BTN_TYPES = [
  {
    id: "mutual",
    label: "Mutual Match",
    description: "When both users have selected each other",
  },
  {
    id: "sitters",
    label: "Sitters Choice",
    description: "Sitters choose which movers they want to match with",
  },
  {
    id: "movers",
    label: "Movers Choice",
    description: "Movers choose which sitters they want to match with",
  },
];

// The kinds of room the form offers. Dating is off the list for now: an event
// already saved as dating keeps that category, and its picker simply opens with
// nothing selected until someone picks one of these.
export const CATEGORIES = [
  { id: "social", label: "Social" },
  { id: "professional", label: "Professional" },
];

// The fields a professional room can belong to (GIS-305). src/contracts/ holds a
// hand-copied twin of gist-firebase-functions/contracts/professionals.json.
export const PROFESSIONAL_FIELDS = professionalsContract.eventFields;

// Default date for a brand-new event: at least 21 days out, landing on the
// next Thursday/Friday/Saturday, at 7:00pm local time.
export const getDefaultEventDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 21);
  const ALLOWED_DAYS = [4, 5, 6]; // Thu, Fri, Sat
  while (!ALLOWED_DAYS.includes(d.getDay())) {
    d.setDate(d.getDate() + 1);
  }
  d.setHours(19, 0, 0, 0);
  return d;
};

export const getEventDataObject = (event) => ({
  id: event?.id || null,
  eventType: event?.eventType || 0,
  maxRound: event?.maxRound || 10,
  name: event?.name || "",
  date: event?.date ? (event.date.toDate ? event.date.toDate() : event.date) : getDefaultEventDate(),
  group: event?.group || { sitters: "Event", movers: "" },
  groupCount: event?.groupCount || 1,
  // A brand-new event starts at a capacity of 100, which the host can change in
  // the Capacity field before saving. It is a visible default, not the silent
  // one GIS-266 removed: the number sits in the form, and saving still refuses
  // a blank or nonsense capacity. An event that already exists keeps whatever
  // it has, including none, so opening an old event never quietly caps it.
  size: event?.size || (event?.id ? { sitters: null, movers: null } : { sitters: 100, movers: 100 }),
  photos: event?.photos || [],
  description: event?.description || "",
  hostname: event?.hostname || "",
  address: event?.address || "",
  movers: event?.movers || [],
  sitters: event?.sitters || [],
  rsvpMovers: event?.rsvpMovers || [],
  rsvpSitters: event?.rsvpSitters || [],
  invitedSitters: event?.invitedSitters || [],
  isActive: event?.isActive ?? true,
  isCompleted: event?.isCompleted ?? false,
  isPrivate: event?.isPrivate ?? false,
  sessionTime: event?.sessionTime || 480,
  delayStart: event?.delayStart || 300,
  eventUsers: event?.eventUsers || {},
  showBadgeTableNumber: event?.showBadgeTableNumber ?? false,
  vendorID: event?.vendorID || "",
  meetupEventId: event?.meetupEventId || "",
  // Extra listing creatives for this event, keyed by variant id ("a" is the base
  // listing and never appears here). The author owns this map outright: the
  // server only reads it. Its server-written counterpart, `listingSyncState`
  // (the Meetup/Eventbrite ids the sync writes back), is deliberately NOT in
  // this allowlist — the edit path spreads this object straight into updateDoc,
  // so listing it here would let a stale form clobber ids the server owns.
  // An empty map means the same thing as no map: one listing per platform.
  listingVariants: event?.listingVariants || {},
  cost: event?.cost ?? 0,
  priceSchedule: event?.priceSchedule || [
    { daysBeforeEvent: 7, seatThreshold: 60, price: 10 },
    { daysBeforeEvent: 3, seatThreshold: 70, price: 15 },
    { daysBeforeEvent: 1, price: 20 },
    { daysBeforeEvent: 0, price: 25 },
  ],
  hostPin: event?.hostPin || null,
  // Whether this event should get an AI video. Unlike videoUrl/videoStatus
  // (server-owned, deliberately not in this allowlist) the host sets this one.
  // A brand-new event starts ticked, so making a video is the default. An
  // existing event that predates the field starts unticked, so opening an old
  // event and saving it never quietly starts paid generation.
  videoEnabled: event?.videoEnabled ?? !event?.id,
  // Mixer check-in switch. On is the mixer everyone has today; only an explicit
  // false turns it off, so an event written before the field existed keeps
  // running as a mixer. `duration` (hours) is how long a no-check-in event runs
  // before the server marks it complete; null means the server default (2h).
  requireCheckIn: event?.requireCheckIn ?? true,
  duration: event?.duration ?? null,
  createdAt: event?.createdAt || null,
  // What kind of room this is (GIS-305). The v2 web app files a room under
  // Friends or Professionals by it and never lists dating rooms. A brand-new
  // event starts as social; an event saved before the picker existed keeps what
  // it has, so opening an old event and saving it never relabels it.
  category: event?.category ?? (event?.id ? null : "social"),
  // A professional room's field, an id from src/contracts/professionals.json.
  // Only a professional room keeps one.
  field: event?.category === "professional" ? (event?.field ?? null) : null,
});
